import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, CreditCard, PiggyBank, Globe, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета валютной ипотеки
 */
const ResultsOverview = ({ results, formData, currencyRates }) => {
  if (!results) return null;

  // Расчет процента переплаты от суммы кредита
  const overpaymentPercent = (results.totalCreditCost / results.loanAmount) * 100;
  
  // Получение символа валюты
  const getCurrencySymbol = () => {
    switch (formData.currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'CHF': return 'Fr';
      case 'JPY': case 'CNY': return '¥';
      default: return formData.currency;
    }
  };
  
  // Форматирование суммы в валюте
  const formatCurrencyAmount = (amount) => {
    return `${getCurrencySymbol()}${formatNumber(amount, 2)}`;
  };

  return (
    <ToolResult title="Результаты расчета валютной ипотеки">
      {/* Ключевые метрики в двух валютах */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Ежемесячный платеж в валюте */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4"/>
            Ежемесячный платеж ({formData.currency})
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {/* Показываем диапазон для дифференцированных платежей, иначе первый платеж */}
              {Math.abs(results.firstMonthlyPayment - results.lastMonthlyPayment) > 0.01
                ? `${formatCurrencyAmount(results.firstMonthlyPayment)} → ${formatCurrencyAmount(results.lastMonthlyPayment)}`
                : formatCurrencyAmount(results.firstMonthlyPayment)
              }
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            {Math.abs(results.firstMonthlyPayment - results.lastMonthlyPayment) > 0.01 
              ? 'Сумма уменьшается с течением времени (дифференцированный платеж)' 
              : 'Фиксированный платеж на весь срок (аннуитетный)'}
          </p>
        </div>

        {/* Ежемесячный платеж в рублях */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Globe className="w-4 h-4"/>
            Ежемесячный платеж (₽)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {Math.abs(results.firstMonthlyPaymentRUB - results.lastMonthlyPaymentRUB) > 0.01
                ? `${formatNumber(results.firstMonthlyPaymentRUB)} ₽ → ${formatNumber(results.lastMonthlyPaymentRUB)} ₽`
                : `${formatNumber(results.firstMonthlyPaymentRUB)} ₽`
              }
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Рассчитано с учетом изменения курса по выбранному сценарию
          </p>
        </div>
        
        {/* Переплата в валюте */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Переплата по ипотеке ({formData.currency})
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatCurrencyAmount(results.totalCreditCost)}
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              ({formatNumber(overpaymentPercent, 1)}% от суммы)
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Включает проценты{results.totalFees > 0 ? ' и комиссии' : ''}
          </p>
        </div>
        
        {/* Переплата в рублях */}
        <div className="rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 p-5 border border-amber-100 dark:border-amber-800/50 shadow-sm">
          <h3 className="text-base font-medium text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Переплата по ипотеке (₽)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-amber-800 dark:text-amber-200" aria-live="polite">
              {formatNumber(results.totalCreditCostRUB)} ₽
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Учитывает прогнозируемое изменение курса {formData.currency} к рублю
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Сумма кредита" 
          value={`${formatCurrencyAmount(results.loanAmount)} (${formatNumber(results.loanAmountRUB)} ₽)`} 
          color="indigo" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Процентная ставка" 
          value={`${formatNumber(results.interestRate, 2)}% годовых`} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Срок ипотеки" 
          value={`${results.loanTermYears} ${formatYearsLabel(results.loanTermYears)} (${results.loanTermMonths} мес.)`} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        {results.effectiveTermMonths !== results.loanTermMonths && (
          <ResultItem 
            label="Фактический срок погашения" 
            value={`${Math.floor(results.effectiveTermMonths / 12)} ${formatYearsLabel(Math.floor(results.effectiveTermMonths / 12))} ${results.effectiveTermMonths % 12} мес.`} 
            color="green"
            icon={<Clock className="w-4 h-4"/>} 
          />
        )}

        <ResultItem 
          label="Валюта кредита" 
          value={`${formData.currency} (${getCurrencySymbol()})`} 
          color="blue"
          icon={<Globe className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Тип платежа" 
          value={results.paymentType === 'annuity' ? 'Аннуитетный (равными платежами)' : 'Дифференцированный (уменьшающийся)'} 
        />
        
        <ResultItem 
          label="Общая сумма выплат в валюте" 
          value={formatCurrencyAmount(results.totalPayment)} 
        />
        
        <ResultItem 
          label="Общая сумма выплат в рублях" 
          value={`${formatNumber(results.totalPaymentRUB)} ₽`} 
          color="blue"
        />
        
        <ResultItem 
          label="Переплата по процентам" 
          value={formatCurrencyAmount(results.totalInterest)} 
          color="orange" 
        />
        
        {results.totalFees > 0 && (
          <ResultItem 
            label="Комиссии и сборы" 
            value={formatCurrencyAmount(results.totalFees)} 
            color="orange" 
          />
        )}
        
        {results.totalEarlyRepayment > 0 && (
          <ResultItem 
            label="Досрочное погашение (всего)" 
            value={formatCurrencyAmount(results.totalEarlyRepayment)} 
            color="green" 
            icon={<PiggyBank className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Курс в начале кредита" 
          value={`1 ${formData.currency} = ${formatNumber(results.initialExchangeRate, 2)} ₽`} 
          color="blue"
          icon={<Globe className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Прогнозируемый курс в конце кредита" 
          value={`1 ${formData.currency} = ${formatNumber(results.finalExchangeRate, 2)} ₽`} 
          color="purple"
          icon={<Globe className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Средний курс за весь период" 
          value={`1 ${formData.currency} = ${formatNumber(results.averageExchangeRate, 2)} ₽`} 
          color="indigo"
          icon={<Globe className="w-4 h-4"/>} 
        />

        {/* Информация о ПСК и валютных рисках */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Полная стоимость кредита и валютные риски</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                ПСК = <span className="font-medium text-orange-600 dark:text-orange-400">{formatNumber(results.effectiveRate || overpaymentPercent, 2)}% годовых</span>. 
                <br />
                Расчет выполнен для выбранного сценария изменения курса валют. Фактические платежи в рублях могут отличаться в зависимости от реального изменения валютного курса.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;